/**
 * Platform appearance (white-label). Read cache-first from the PUBLIC `/api/appearance`
 * so the login screen + shell brand themselves instantly (warmed at login, revalidated
 * ~60s so a super-admin change propagates). Applies the accent color as a CSS variable
 * and sets the document title. Falls back to the built-in defaults offline / pre-fetch.
 */
import { useEffect } from "react";
import { useServices } from "../app/context.js";
import { useCachedResource } from "./useCachedResource.js";
import { CACHE_KEYS } from "./sessionWarmup.js";
import type { Appearance } from "@genz/platform";

export const DEFAULT_APPEARANCE: Appearance = {
  app_name: "Image Maker Gen Z",
  tagline: "Turn reference photos into marketplace-ready phone-case mockups.",
  short_mark: "IM",
  accent_color: null,
  logo_url: null,
};

export function useAppearance(): Appearance {
  const { client } = useServices();
  const res = useCachedResource<Appearance>(CACHE_KEYS.appearance, () => client.getAppearance());
  const a = res.data ?? DEFAULT_APPEARANCE;

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = a.app_name;
    const root = document.documentElement;
    if (a.accent_color) root.style.setProperty("--brand-accent", a.accent_color);
    else root.style.removeProperty("--brand-accent");
  }, [a.app_name, a.accent_color]);

  return a;
}
